var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { P, Pivot, R } from "apparatus/library/common";
import { BasicShapes } from "apparatus/library/shapes/common";
import { Shape } from "paper";
const BOUND = 10000;
function avg(a, key1, key2) {
    return (a[key1] + a[key2]) / 2;
}
export class Rectangle {
    constructor() {
        // TODO min size
        this.drivers = [
            {
                // Top Left
                type: "rectangle",
                minX: -BOUND,
                minY: -BOUND,
                maxX: (a => a.right - 8),
                maxY: (a => a.bottom - 8),
                toAppearance: (f, p) => f({
                    left: p.x,
                    top: p.y,
                }),
                fromAppearance: a => P(a.left, a.top),
                scale: 1.0,
                constraints(a, modifiers) {
                    if (modifiers.shift) {
                        // Force square.
                        const width = a.right - a.left;
                        const height = a.bottom - a.top;
                        const size = Math.max(width, height);
                        return { top: a.bottom - size, left: a.right - size };
                    }
                    return;
                }
            },
            {
                // Top Right
                type: "rectangle",
                maxX: BOUND,
                minX: (a => a.left + 8),
                minY: -BOUND,
                maxY: (a => a.bottom - 8),
                toAppearance: (f, p) => f({
                    right: p.x,
                    top: p.y,
                }),
                fromAppearance: a => P(a.right, a.top),
                scale: 1.0,
                constraints(a, modifiers) {
                    if (modifiers.shift) {
                        // Force square.
                        const width = a.right - a.left;
                        const height = a.bottom - a.top;
                        const size = Math.max(width, height);
                        return { top: a.bottom - size, right: a.left + size };
                    }
                    return;
                }
            },
            {
                // Right
                type: "rectangle",
                minX: (a => a.left + 8),
                maxX: BOUND,
                minY: a => avg(a, "top", "bottom"),
                maxY: a => avg(a, "top", "bottom"),
                toAppearance: (f, p) => f({
                    right: p.x,
                }),
                fromAppearance: a => P(a.right, avg(a, "top", "bottom")),
                scale: 1.0,
            },
            {
                // Left
                type: "rectangle",
                minX: -BOUND,
                maxX: (a => a.right - 8),
                minY: a => avg(a, "top", "bottom"),
                maxY: a => avg(a, "top", "bottom"),
                toAppearance: (f, p) => f({
                    left: p.x,
                }),
                fromAppearance: a => P(a.left, avg(a, "top", "bottom")),
                scale: 1.0,
            },
            {
                // Top
                type: "rectangle",
                minX: a => avg(a, "left", "right"),
                maxX: a => avg(a, "left", "right"),
                minY: -BOUND,
                maxY: a => a.bottom - 8,
                toAppearance: (f, p) => f({
                    top: p.y,
                }),
                fromAppearance: a => P(avg(a, "left", "right"), a.top),
                scale: 1.0,
            },
            {
                // Bottom
                type: "rectangle",
                minX: a => avg(a, "left", "right"),
                maxX: a => avg(a, "left", "right"),
                minY: a => a.top + 8,
                maxY: BOUND,
                toAppearance: (f, p) => f({
                    bottom: p.y,
                }),
                fromAppearance: a => P(avg(a, "left", "right"), a.bottom),
                scale: 1.0,
            },
            {
                // Bottom Left
                type: "rectangle",
                minX: -BOUND,
                maxX: (a => a.right - 8),
                minY: (a => a.top + 8),
                maxY: BOUND,
                toAppearance: (f, p) => f({
                    left: p.x,
                    bottom: p.y,
                }),
                fromAppearance: a => P(a.left, a.bottom),
                scale: 1.0,
                constraints(a, modifiers) {
                    if (modifiers.shift) {
                        // Force square.
                        const width = a.right - a.left;
                        const height = a.bottom - a.top;
                        const size = Math.max(width, height);
                        return { bottom: a.top + size, left: a.right - size };
                    }
                    return;
                }
            },
            {
                // Bottom right
                type: "rectangle",
                minX: (a => a.left + 8),
                minY: (a => a.top + 8),
                maxX: BOUND,
                maxY: BOUND,
                toAppearance: (f, p) => f({
                    right: p.x,
                    bottom: p.y,
                }),
                fromAppearance: a => P(a.right, a.bottom),
                scale: 1.0,
                constraints(a, modifiers) {
                    if (modifiers.shift) {
                        // Force square.
                        const width = a.right - a.left;
                        const height = a.bottom - a.top;
                        const size = Math.max(width, height);
                        return { bottom: a.top + size, right: a.left + size };
                    }
                    return;
                }
            }
        ];
        /** Pivot is used to pin the origin (top, left), but we still want to rotate based on shape center */
        this.shouldUseBoundsCenter = true;
    }
    static convertToLatest(input) {
        if (("width" in input)) {
            // Convert v0: Set top and left, by halving dimensions.
            const { width, height } = input, keep = __rest(input, ["width", "height"]);
            return Object.assign({ top: -input.height / 2, left: -input.width / 2, bottom: input.height / 2, right: input.width / 2 }, keep);
        }
        return input;
    }
    render(appearance) {
        let cornerRadius = 0;
        const width = appearance.right - appearance.left;
        const height = appearance.bottom - appearance.top;
        if (appearance.roundedCorner) {
            cornerRadius = Math.min(Math.min(width / 2, height / 2), appearance.cornerRadius);
        }
        const graphic = Shape.Rectangle(R(appearance.left, appearance.top, width, height, Pivot.TOP_LEFT), cornerRadius);
        BasicShapes.applyStyle(graphic, appearance);
        return {
            graphic: graphic,
            pivot: P(0, 0), // Pin to origin
            hitShape: graphic.rectHitShape(),
            snapping: {
                type: "bounds", bounds: graphic.bounds
            }
        };
    }
}
Rectangle.properties = {
    label: "Rectangle",
    defaultAppearance: Object.assign({ left: -100, top: -50, right: 100, bottom: 50, roundedCorner: false, cornerRadius: 10 }, BasicShapes.defaultAppearance),
    appearanceSpecs: [
        ...BasicShapes.specs,
        BasicShapes.header,
        {
            spec: "checkbox", key: "roundedCorner", label: "Rounded corners",
        },
        {
            spec: "slider", key: "cornerRadius", label: "Corner size",
            min: 1, max: 30,
            visible: a => a.roundedCorner
        }
    ],
};
